import supabase from '../supabase/supabase';

const TABLES = {
  learners: 'hms_academy_learners_1715725000000',
  courses: 'hms_academy_courses_1715725000000',
  versions: 'hms_academy_course_versions_1715725000000',
  enrollments: 'hms_academy_enrollments_1715726000000'
};

function serviceError(code, message, cause) {
  const error = new Error(message);
  error.code = code;
  if (cause) error.cause = cause;
  return error;
}

function unwrap({ data, error }, operation) {
  if (error) {
    throw serviceError(
      'SUPABASE_ERROR',
      `Academy ${operation} lookup failed: ${error.message}`,
      error
    );
  }

  return data || [];
}

function requireSingleRow(rows, missingCode, label) {
  if (rows.length === 0) {
    throw serviceError(missingCode, `No matching Academy ${label} was found.`);
  }

  if (rows.length > 1) {
    throw serviceError(
      'ACADEMY_LOOKUP_AMBIGUOUS',
      `More than one matching Academy ${label} was found.`
    );
  }

  return rows[0];
}

export async function getCurrentUserEnrollmentForCourse({
  courseKey = 'psykososialt-arbeidsmiljo-praktisk-handling',
  version: requestedVersion = '1.0',
  languageCode = 'nb',
  includeCompleted = false
} = {}) {
  const { data: authData, error: authError } = await supabase.auth.getUser();

  if (authError) {
    throw serviceError('SUPABASE_ERROR', authError.message, authError);
  }

  const authenticatedUser = authData?.user;
  if (!authenticatedUser) {
    throw serviceError('NO_AUTH_SESSION', 'An authenticated session is required.');
  }

  const learner = requireSingleRow(
    unwrap(
      await supabase
        .from(TABLES.learners)
        .select('id')
        .eq('user_id', authenticatedUser.id)
        .eq('status', 'active')
        .limit(2),
      'learner'
    ),
    'NO_ACTIVE_LEARNER',
    'active learner'
  );

  const course = requireSingleRow(
    unwrap(
      await supabase
        .from(TABLES.courses)
        .select('id')
        .is('organization_id', null)
        .eq('course_key', courseKey)
        .eq('status', 'approved')
        .limit(2),
      'course'
    ),
    'COURSE_NOT_FOUND',
    'course'
  );

  const courseVersion = requireSingleRow(
    unwrap(
      await supabase
        .from(TABLES.versions)
        .select('id')
        .eq('course_id', course.id)
        .eq('version', requestedVersion)
        .eq('language_code', languageCode)
        .eq('status', 'approved')
        .limit(2),
      'course version'
    ),
    'COURSE_VERSION_NOT_FOUND',
    'course version'
  );

  const enrollmentQuery = supabase
    .from(TABLES.enrollments)
    .select('id,status,source,activated_at,created_at')
    .eq('learner_id', learner.id)
    .eq('course_version_id', courseVersion.id);

  const enrollmentRows = unwrap(
    includeCompleted
      ? await enrollmentQuery
        .in('status', ['active', 'completed'])
        .order('created_at', { ascending: false })
        .limit(20)
      : await enrollmentQuery
        .eq('status', 'active')
        .limit(2),
    'enrollment'
  );

  let enrollment;

  if (!includeCompleted) {
    enrollment = requireSingleRow(
      enrollmentRows,
      'NO_ACTIVE_ENROLLMENT',
      'active enrollment'
    );
  } else {
    const activeEnrollments = enrollmentRows.filter(
      (row) => row.status === 'active'
    );

    if (activeEnrollments.length > 1) {
      throw serviceError(
        'ACADEMY_LOOKUP_AMBIGUOUS',
        'More than one matching Academy active enrollment was found.'
      );
    }

    enrollment = activeEnrollments[0] ||
      enrollmentRows.find((row) => row.status === 'completed');

    if (!enrollment) {
      throw serviceError(
        'NO_ACTIVE_ENROLLMENT',
        'No matching active or completed Academy enrollment was found.'
      );
    }
  }

  return {
    learnerId: learner.id,
    enrollmentId: enrollment.id,
    courseId: course.id,
    courseVersionId: courseVersion.id,
    enrollmentStatus: enrollment.status,
    enrollmentSource: enrollment.source,
    activatedAt: enrollment.activated_at
  };
}